import { useCallback, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import deleteUserApi from '@/api/delete/user/delete-user';
import getUserApi from '@/api/get/user-management/get-user';
import updateUserApi from '@/api/patch/user/update-user';
import Alert from '@/components/alert/alert-component';
import SubmitCard from '@/components/card/submit-card-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation, useQuery } from '@/hooks/use-api';
import { APP_CONTEXT } from '@/utils/context';
import resendUserEmailApi from '@/api/patch/user/resend-email';
import Loader from '@/components/loader/loader-component';
import Styled from './user-edit-style';
import UserForm from './user-form';

/**
 * create new user
 * @returns {JSX.Element}
 */
const UserEdit = () => {
  const { setActiveChildSideNav } = useContext(APP_CONTEXT);
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, loading, error } = useQuery(getUserApi, id);
  const [updateUser, { loading: updateUserLoading }] =
    useMutation(updateUserApi);

  const onUpdateUser = async (newUserDetail) =>
    updateUser(id, newUserDetail)
      .then(() => {
        navigate('/app/user-management');
        Alert({
          icon: 'success',
          text: 'User updated successfully',
        });
      })
      .catch(() =>
        Alert({
          icon: 'error',
          text: 'Failed to update user',
        }),
      );

  const [deleteUser, { loading: deleteUserLoading }] =
    useMutation(deleteUserApi);

  const [resendEmailVerify, { loading: resendEmailLoading }] =
    useMutation(resendUserEmailApi);

  const launchDeleteConfirmation = useCallback(() => {
    ConfirmationModal({
      title: 'Delete User',
      text: `Are you sure you want to delete user?`,
      onConfirm: () =>
        deleteUser(id).then(() => {
          navigate('/app/user-management');
          Alert({
            icon: 'success',
            text: 'User deleted successfully',
          });
        }),
    });
  }, [deleteUser, id, navigate]);

  const launchResendConfirmation = useCallback(() => {
    ConfirmationModal({
      title: 'Resend Email Verification',
      text: `Are you sure you want to send the register email to the user?`,
      onConfirm: () =>
        resendEmailVerify(id).then(() => {
          Alert({
            icon: 'success',
            text: 'Email sent successfully',
          });
        }),
    });
  }, [id, resendEmailVerify]);

  const isLoading =
    deleteUserLoading || updateUserLoading || resendEmailLoading;

  useEffect(() => {
    setActiveChildSideNav('users');
  }, [setActiveChildSideNav]);

  if (loading) {
    return (
      <Styled.Main>
        <Loader loading />
      </Styled.Main>
    );
  }

  if (error) {
    return <div>error</div>;
  }

  return (
    <Styled.Main>
      <FormWrapper defaultValues={data ?? []} onSubmit={onUpdateUser}>
        <UserForm
          type="edit"
          isLoading={isLoading}
          resendEmail={launchResendConfirmation}
          userId={id}
        />
        <SubmitCard
          padding="0"
          primaryButtonTitle="Save"
          primaryButtonType="submit"
          primaryButtonDisabled={false}
          primaryButtonWidth="112px"
          secondaryButtonType="button"
          secondaryButtonDisabled={false}
          isLoading={isLoading}
          secondaryButtonWidth="112px"
          secondaryPermission="user.delete"
          submitClick={() => {}}
          submitClickSecondary={() => {
            launchDeleteConfirmation();
          }}
          secondaryButtonTitle="Delete"
        />
      </FormWrapper>
    </Styled.Main>
  );
};

UserEdit.propTypes = {};

export default UserEdit;
